import { useEffect, useState } from 'react';
import { axiosClient, apiClient } from '../../auth-lib';
import { Button, Modal, Input, Select, Skeleton, useToast } from '../../design-system';
import '../styles/EmployeeExit.css';

// Taking someone out of the firm, in the order the work actually has to happen:
//
//   1. record the last working day   (auth-service — owns the employment record)
//   2. hand their live work over     (hrms — owns the tasks, clients, chats)
//   3. remove the account            (auth-service, refused until step 2 is done)
//
// The order is the point. Deactivating first is the bug: their compliance
// enrolment defaults keep generating tasks in their name, their clients have no
// manager, and their chats route to nobody — silently, for months.
//
// Two services, so two clients: axiosClient talks to auth-service, apiClient to
// the business app. Mixing them up is the commonest mistake on this screen.

const dateInput = (value) => (value ? new Date(value).toISOString().slice(0, 10) : '');

function EmployeeExitModal({ employee, colleagues, onClose, onChanged }) {
  const toast = useToast();
  const [step, setStep] = useState('exit'); // exit | handover | done
  const [exitDate, setExitDate] = useState(dateInput(employee?.employment?.exitDate) || dateInput(new Date()));
  const [exitReason, setExitReason] = useState(employee?.employment?.exitReason || '');
  const [successorId, setSuccessorId] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The preview is read-only and generates nothing, so it is safe to load as
  // soon as the dialog opens — the person deciding needs to see what is at
  // stake before they commit to a date.
  useEffect(() => {
    if (!employee) return;
    let cancelled = false;
    setLoadingPreview(true);
    apiClient
      .get(`/api/employees/${employee._id}/handover`)
      .then((res) => !cancelled && setPreview(res.data))
      .catch(() => !cancelled && setPreview(null))
      .finally(() => !cancelled && setLoadingPreview(false));
    return () => {
      cancelled = true;
    };
  }, [employee]);

  const recordExit = async () => {
    setError('');
    setBusy(true);
    try {
      await axiosClient.post(`/users/${employee._id}/exit`, { exitDate, exitReason });
      toast.success('Last working day recorded');
      onChanged?.();
      setStep('handover');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record the exit');
    } finally {
      setBusy(false);
    }
  };

  const runHandover = async () => {
    setError('');
    setBusy(true);
    try {
      const res = await apiClient.post(`/api/employees/${employee._id}/handover`, { successorId });
      // auth-service cannot see tasks or clients, so it records the handover on
      // this service's say-so — which is why this call follows rather than
      // being folded into the one above.
      await axiosClient.post(`/users/${employee._id}/handover-complete`, { handoverToId: successorId });
      toast.success(`Moved ${res.data.totalMoved} assignments to ${res.data.successor.name}`);
      setPreview({ ...preview, ...res.data.remaining, blockers: res.data.blockers });
      onChanged?.();
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not hand the work over');
    } finally {
      setBusy(false);
    }
  };

  if (!employee) return null;

  const total = preview?.total ?? 0;
  const eligible = colleagues.filter(
    (c) => c._id !== employee._id && c.isActive !== false && !c.employment?.exitDate
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Offboard ${employee.name}`}
      footer={
        step === 'exit' ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={recordExit} loading={busy} disabled={!exitDate}>
              Record last working day
            </Button>
          </>
        ) : step === 'handover' ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>Do this later</Button>
            <Button variant="primary" onClick={runHandover} loading={busy} disabled={!successorId || total === 0}>
              {total === 0 ? 'Nothing to hand over' : `Hand over ${total} items`}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={onClose}>Done</Button>
        )
      }
    >
      <div className="exit-modal">
        {error && <div className="form-error" role="alert">{error}</div>}

        {step === 'exit' && (
          <>
            <Input
              label="Last working day"
              id="exit-date"
              type="date"
              value={exitDate}
              onChange={(e) => setExitDate(e.target.value)}
              disabled={busy}
              // Notice periods are the norm, so a future date is expected, not
              // an error — and recording one does NOT disable their login.
              helperText="They keep their login until the account is removed — notice periods are normal."
            />
            <Input
              label="Reason (optional)"
              id="exit-reason"
              value={exitReason}
              onChange={(e) => setExitReason(e.target.value)}
              disabled={busy}
            />
          </>
        )}

        {step !== 'exit' && (
          <Select
            label="Hand their work to"
            id="successor"
            value={successorId}
            onChange={(e) => setSuccessorId(e.target.value)}
            disabled={busy || step === 'done'}
          >
            <option value="">-- Choose a colleague --</option>
            {eligible.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}{c.employment?.designation ? ` · ${c.employment.designation}` : ''}
              </option>
            ))}
          </Select>
        )}

        <div className="exit-section">
          <h4 className="exit-section-title">
            {step === 'done' ? 'Still assigned to them' : 'Currently assigned to them'}
          </h4>

          {loadingPreview ? (
            <Skeleton height={80} />
          ) : total === 0 ? (
            <p className="exit-empty">Nothing is assigned to them — nothing to hand over.</p>
          ) : (
            <ul className="exit-scope-list">
              {preview.scopes.map((s) => (
                <li key={s.key} className="exit-scope">
                  <span className="exit-scope-count">{s.count}</span>
                  <span className="exit-scope-label">
                    {s.label}
                    {/* The consequence, not the field name — the person
                        deciding needs to know what breaks if this is skipped. */}
                    <em className="exit-scope-impact">{s.impact}</em>
                  </span>
                </li>
              ))}
            </ul>
          )}

          {preview?.blockers?.length > 0 && (
            <div className="exit-blockers">
              <strong>These cannot be transferred and need doing by hand:</strong>
              <ul>
                {preview.blockers.map((b) => (
                  <li key={b.key}>
                    {b.label} — <span className="exit-blocker-detail">{b.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default EmployeeExitModal;
